/**
 * 📄 MODAL DÉTAIL FACTURE FOURNISSEUR
 * ====================================
 * Modal pour afficher tous les détails d'une facture fournisseur logistics
 *
 * Fonctionnalités:
 * - Affichage complet : En-tête, Fournisseur, Items, Totaux
 * - Support multi-dossiers (récapitulatif par dossier)
 * - Liste des lignes de coût incluses
 * - Historique paiements (si applicable - Phase 4)
 * - Actions : Télécharger pièce jointe (si attachement)
 * - Design professionnel avec Cards et Descriptions
 *
 * Pattern source:
 * - Inspiré de: InvoiceDetailModal.tsx
 * - Adapté pour: Factures fournisseurs avec support multi-dossiers
 *
 * @see src/gestion-logistics/hooks/useSupplierBills.ts
 * @see src/gestion-logistics/types/invoice.types.ts
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Descriptions,
  Table,
  Button,
  Space,
  Tag,
  Divider,
  message,
  Spin,
  Card,
  Row,
  Col,
  Typography,
  Alert,
  Empty,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  DownloadOutlined,
  CalendarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ShopOutlined,
  PaperClipOutlined,
  DollarOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import useSupplierBills from '../hooks/useSupplierBills';
import useSupplierPayments from '../hooks/useSupplierPayments';
import { SupplierBill, SupplierBillItem } from '../types/invoice.types';
import { SupplierPayment } from '../types/payment.types';
import SupplierPaymentRecordModal from './SupplierPaymentRecordModal';
import { useLogisticsPermissions } from '../hooks/useLogisticsPermissions';
import API_CONFIG from '../../config/api';
import { useSelection } from '../../SelectionContext';

const { Title, Text } = Typography;

dayjs.locale('fr');

interface SupplierBillDetailModalProps {
  visible: boolean;
  bill: SupplierBill | null;
  onClose: () => void;
  onRefresh?: () => void;
}

const SupplierBillDetailModal: React.FC<SupplierBillDetailModalProps> = ({
  visible,
  bill,
  onClose,
  onRefresh,
}) => {
  const [loading, setLoading] = useState(false);
  const [billDetail, setBillDetail] = useState<SupplierBill | null>(null);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);

  const { fetchSupplierBillById } = useSupplierBills();
  const {
    supplierPayments,
    fetchPaymentsBySupplierBill,
    deletePayment,
    loading: paymentsLoading,
  } = useSupplierPayments();
  const permissions = useLogisticsPermissions();
  const { selectedCompany } = useSelection();

  // Fonction utilitaire pour formater les montants
  const formatAmount = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    const formatted = formatAmount(amount);
    return `${formatted} XOF`;
  };

  // Charger les détails de la facture (avec items et payments)
  useEffect(() => {
    if (visible && bill) {
      fetchBillDetail();
    }
  }, [visible, bill]);

  const fetchBillDetail = async () => {
    if (!bill) return;

    setLoading(true);
    try {
      const fullBill = await fetchSupplierBillById(bill.id);
      if (fullBill) {
        setBillDetail(fullBill);
        // Charger aussi les paiements
        await fetchPaymentsBySupplierBill(bill.id);
      }
    } catch (error) {
      console.error('Erreur chargement facture fournisseur:', error);
      message.error('Erreur lors du chargement de la facture');
    } finally {
      setLoading(false);
    }
  };

  const buildAttachmentUrl = (billId: number) => {
    const baseUrl = `${API_CONFIG.API_URL}/logistics/supplier-bills/${billId}/attachment`;
    if (!selectedCompany) {
      return baseUrl;
    }
    return `${baseUrl}?company_id=${encodeURIComponent(String(selectedCompany))}`;
  };

  // Télécharger la pièce jointe
  const handleDownloadAttachment = async () => {
    if (!billDetail?.attachment_url) {
      message.error('Aucune pièce jointe disponible');
      return;
    }

    try {
      const response = await axios.get(buildAttachmentUrl(billDetail.id), {
        responseType: 'blob',
      });
      const contentType =
        response.headers['content-type'] ||
        billDetail.attachment_mime_type ||
        'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      const opened = window.open(blobUrl, '_blank', 'noopener,noreferrer');

      if (!opened) {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = billDetail.attachment_filename || 'piece-jointe';
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (error) {
      console.error('Erreur telechargement piece jointe:', error);
      message.error('Impossible d\'ouvrir la piÇùce jointe');
    }
    message.success('Téléchargement de la pièce jointe...');
  };

  // Parser le JSON shipment_summary
  const parseShipmentSummary = (summaryJson?: string): any => {
    if (!summaryJson) return null;
    try {
      return JSON.parse(summaryJson);
    } catch (error) {
      console.error('Erreur parsing shipment_summary:', error);
      return null;
    }
  };

  // Handler suppression paiement
  const handleDeletePayment = async (paymentId: number) => {
    const success = await deletePayment(paymentId);
    if (success) {
      // Rafraîchir les détails de la facture (balance_due sera mis à jour par trigger)
      await fetchBillDetail();
      if (onRefresh) onRefresh();
    }
  };

  // Handler succès enregistrement paiement
  const handlePaymentSuccess = async () => {
    setPaymentModalVisible(false);
    // Rafraîchir les détails et paiements
    await fetchBillDetail();
    if (onRefresh) onRefresh();
  };

  // Rendu du statut
  const renderStatus = (status: string) => {
    const statusConfig: Record<
      string,
      { color: string; icon: React.ReactNode; label: string }
    > = {
      paid: {
        color: 'success',
        icon: <CheckCircleOutlined />,
        label: 'Payée',
      },
      partial: {
        color: 'warning',
        icon: <ClockCircleOutlined />,
        label: 'Partiellement payée',
      },
      unpaid: {
        color: 'default',
        icon: <ClockCircleOutlined />,
        label: 'Non payée',
      },
      overdue: {
        color: 'error',
        icon: <ExclamationCircleOutlined />,
        label: 'En retard',
      },
    };

    const config = statusConfig[status] || statusConfig.unpaid;

    return (
      <Tag
        color={config.color}
        icon={config.icon}
        style={{ fontSize: '14px', padding: '4px 12px' }}
      >
        {config.label}
      </Tag>
    );
  };

  // Colonnes de la table des items
  const itemsColumns: ColumnsType<SupplierBillItem> = [
    {
      title: '#',
      dataIndex: 'line_order',
      key: 'line_order',
      width: 50,
      align: 'center',
      render: (order: number) => order || '-',
    },
    {
      title: 'Dossier',
      key: 'shipment',
      width: 100,
      render: (_, record) => (
        <Text strong style={{ color: '#1890ff', fontSize: '12px' }}>
          #{record.shipment_number || record.shipment_id}
        </Text>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record) => (
        <div>
          <div>{text || 'N/A'}</div>
          {record.notes && (
            <div style={{ fontSize: '11px', color: '#666' }}>
              Note: {record.notes}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Quantité',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'center',
      render: (qty: number) => `${qty || 1}`,
    },
    {
      title: 'Prix Unitaire',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 130,
      align: 'right',
      render: (price: number) => formatCurrency(price),
    },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (amount: number) => (
        <Text strong style={{ fontSize: '14px' }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <span>Détails Facture Fournisseur</span>
          {billDetail && renderStatus(billDetail.payment_status)}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
        billDetail?.attachment_url && (
          <Button
            key="download"
            type="default"
            icon={<DownloadOutlined />}
            onClick={handleDownloadAttachment}
          >
            Télécharger pièce jointe
          </Button>
        ),
      ]}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : billDetail ? (
        <div>
          {/* En-tête avec numéro de facture et montant total */}
          <Card
            style={{
              marginBottom: 16,
              backgroundColor: '#f0f5ff',
              border: '1px solid #adc6ff',
            }}
          >
            <Row gutter={[16, 16]} align="middle">
              <Col span={12}>
                <Space direction="vertical" size={0}>
                  <Text type="secondary">Facture Fournisseur N°</Text>
                  <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                    {billDetail.bill_number}
                  </Title>
                  {/* 🔧 FIX: Utilisation condition stricte pour éviter affichage "0" */}
                  {billDetail.internal_reference ? (
                    <Text type="secondary">
                      Réf interne: {billDetail.internal_reference}
                    </Text>
                  ) : null}
                  {billDetail.is_multi_shipment ? (
                    <Tag color="warning" icon={<FileTextOutlined />}>
                      Multi-dossiers ({billDetail.shipments_count || 0} dossier
                      {(billDetail.shipments_count || 0) > 1 ? 's' : ''})
                    </Tag>
                  ) : null}
                </Space>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Space
                  direction="vertical"
                  size={0}
                  style={{ width: '100%', alignItems: 'flex-end' }}
                >
                  <Text type="secondary">Montant Total</Text>
                  <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                    {formatCurrency(billDetail.total_amount)}
                  </Title>
                  {/* 🔧 FIX: Condition stricte pour éviter "0" */}
                  {billDetail.paid_amount > 0 ? (
                    <Text style={{ color: '#52c41a', fontSize: '14px' }}>
                      Payé: {formatCurrency(billDetail.paid_amount)}
                    </Text>
                  ) : null}
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Alerte si facture payée */}
          {billDetail.payment_status === 'paid' && billDetail.paid_amount > 0 ? (
              <Alert
                message="Facture Entièrement Payée"
                description={
                  <div>
                    <p>
                      <strong>Montant payé:</strong>{' '}
                      {formatCurrency(billDetail.paid_amount)}
                    </p>
                    {billDetail.updated_at && (
                      <p>
                        <strong>Dernière mise à jour:</strong>{' '}
                        {dayjs(billDetail.updated_at).format('DD MMMM YYYY')}
                      </p>
                    )}
                  </div>
                }
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
            ) : null}

          {/* Informations générales */}
          <Card title="Informations Générales" size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item
                label={
                  <Space>
                    <ShopOutlined />
                    <span>Fournisseur</span>
                  </Space>
                }
              >
                <Text strong>{billDetail.supplier_name}</Text>
              </Descriptions.Item>

              <Descriptions.Item
                label={
                  <Space>
                    <CalendarOutlined />
                    <span>Date de facture</span>
                  </Space>
                }
              >
                {dayjs(billDetail.bill_date).format('DD MMMM YYYY')}
              </Descriptions.Item>

              {billDetail.due_date ? (
                <Descriptions.Item
                  label={
                    <Space>
                      <CalendarOutlined />
                      <span>Date d'échéance</span>
                    </Space>
                  }
                >
                  {dayjs(billDetail.due_date).format('DD MMMM YYYY')}
                </Descriptions.Item>
              ) : null}

              <Descriptions.Item label="Statut paiement">
                {renderStatus(billDetail.payment_status)}
              </Descriptions.Item>

              <Descriptions.Item label="Total lignes">
                <Tag color="blue">
                  {billDetail.total_lines_count || billDetail.items?.length || 0}{' '}
                  ligne(s)
                </Tag>
              </Descriptions.Item>

              {/* 🔧 FIX: Pièce jointe toujours visible (même après paiement) */}
              {billDetail.attachment_url ? (
                <Descriptions.Item
                  label={
                    <Space>
                      <PaperClipOutlined />
                      <span>Pièce jointe</span>
                    </Space>
                  }
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadAttachment}
                  >
                    {billDetail.attachment_filename || "Télécharger la pièce jointe"}
                  </Button>
                </Descriptions.Item>
              ) : null}
            </Descriptions>

            {billDetail.notes ? (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <div>
                  <Text type="secondary">Notes:</Text>
                  <div style={{ marginTop: 4 }}>{billDetail.notes}</div>
                </div>
              </>
            ) : null}
          </Card>

          {/* Récapitulatif multi-dossiers */}
          {billDetail.is_multi_shipment && billDetail.shipment_summary ? (
            <Card
              title="Récapitulatif par Dossier"
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Alert
                message="Facture Multi-Dossiers"
                description={`Cette facture couvre ${billDetail.shipments_count} dossier(s) pour un total de ${billDetail.total_lines_count} ligne(s).`}
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
              />
              <pre
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(
                  parseShipmentSummary(billDetail.shipment_summary),
                  null,
                  2
                )}
              </pre>
            </Card>
          ) : null}

          {/* Lignes de coût incluses */}
          <Card title="Lignes de Coût Incluses" size="small" style={{ marginBottom: 16 }}>
            <Table
              columns={itemsColumns}
              dataSource={billDetail.items || []}
              rowKey="id"
              size="small"
              pagination={false}
              locale={{
                emptyText: 'Aucune ligne disponible',
              }}
              summary={(data) => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5} align="right">
                      <Text strong>TOTAL:</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                        {formatCurrency(billDetail.total_amount)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </Card>

          {/* Totaux et Paiements */}
          <Card size="small">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Descriptions
                  title="Détails Montants"
                  column={1}
                  size="small"
                  bordered
                >
                  <Descriptions.Item label="Montant Total">
                    <Text strong style={{ fontSize: '16px' }}>
                      {formatCurrency(billDetail.total_amount)}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Montant Payé">
                    <Text style={{ color: '#52c41a', fontSize: '14px' }}>
                      {formatCurrency(billDetail.paid_amount)}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Solde Restant">
                    <Text
                      strong
                      style={{
                        fontSize: '16px',
                        color:
                          billDetail.balance_due > 0 ? '#ff4d4f' : '#52c41a',
                      }}
                    >
                      {formatCurrency(billDetail.balance_due)}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>
              </Col>

              <Col xs={24} sm={12}>
                <Card
                  title="Historique Règlements"
                  size="small"
                  extra={
                    billDetail.balance_due > 0 &&
                    permissions.canCreateSupplierPayments && (
                      <Button
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => setPaymentModalVisible(true)}
                      >
                        Nouveau Règlement
                      </Button>
                    )
                  }
                >
                  {supplierPayments.length > 0 ? (
                    <Table
                      dataSource={supplierPayments}
                      rowKey="id"
                      size="small"
                      pagination={false}
                      columns={[
                        {
                          title: 'Date',
                          dataIndex: 'payment_date',
                          key: 'payment_date',
                          width: 100,
                          render: (date: string) =>
                            dayjs(date).format('DD/MM/YYYY'),
                        },
                        {
                          title: 'Montant',
                          dataIndex: 'amount',
                          key: 'amount',
                          align: 'right',
                          width: 120,
                          render: (amount: number) => (
                            <Text strong style={{ color: '#52c41a' }}>
                              {formatCurrency(amount)}
                            </Text>
                          ),
                        },
                        {
                          title: 'Mode',
                          dataIndex: 'payment_mode_name',
                          key: 'payment_mode_name',
                          width: 100,
                          render: (mode: string) => mode || '-',
                        },
                        {
                          title: 'Actions',
                          key: 'actions',
                          width: 60,
                          align: 'center',
                          render: (_, record: SupplierPayment) =>
                            permissions.canDeleteSupplierPayments && (
                              <Popconfirm
                                title="Supprimer ce règlement ?"
                                description="Le solde de la facture sera recalculé automatiquement."
                                onConfirm={() => handleDeletePayment(record.id)}
                                okText="Supprimer"
                                cancelText="Annuler"
                                okButtonProps={{ danger: true }}
                              >
                                <Tooltip title="Supprimer">
                                  <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                  />
                                </Tooltip>
                              </Popconfirm>
                            ),
                        },
                      ]}
                    />
                  ) : (
                    <Empty
                      description="Aucun règlement enregistré"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                      {billDetail.balance_due > 0 &&
                        permissions.canCreateSupplierPayments && (
                          <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => setPaymentModalVisible(true)}
                          >
                            Enregistrer le 1er Règlement
                          </Button>
                        )}
                    </Empty>
                  )}
                </Card>
              </Col>
            </Row>
          </Card>
        </div>
      ) : (
        <Empty description="Aucune donnée disponible" />
      )}

      {/* Modal enregistrement paiement */}
      <SupplierPaymentRecordModal
        visible={paymentModalVisible}
        onClose={() => setPaymentModalVisible(false)}
        supplierBill={billDetail}
        onSuccess={handlePaymentSuccess}
      />
    </Modal>
  );
};

export default SupplierBillDetailModal;
